import { Injectable } from '@angular/core';
import { SearchHistory, SearchOptions } from '../models/e621.models';

/**
 * Service responsible for managing search history and session persistence.
 * Handles save/load/update of search history entries and last session resumption.
 * Extracted from SearchComponent to reduce component complexity.
 */
@Injectable({
  providedIn: 'root',
})
export class SearchHistoryService {
  private readonly SEARCH_HISTORY_KEY = 'e621-search-history';
  private readonly LAST_SESSION_KEY = 'e621-last-session';

  /**
   * Check if search query has explicit ordering (not random)
   */
  hasExplicitOrder(query: string): boolean {
    const hasOrderTag = /order:\w+/.test(query);

    if (!hasOrderTag) {
      return true;
    }

    const isRandomOrder = /order:random/.test(query);
    return !isRandomOrder;
  }

  /**
   * Save search to history with current position
   */
  saveSearchHistory(
    query: string,
    currentPage: number,
    totalItemsLoaded: number,
    currentPosition?: number,
    searchOptions?: SearchOptions,
  ): void {
    try {
      const hasExplicit = this.hasExplicitOrder(query);

      const searchHistory: SearchHistory = {
        query: query,
        timestamp: Date.now(),
        hasExplicitOrder: hasExplicit,
        currentPage: currentPage,
        totalItemsLoaded: totalItemsLoaded,
        ...(hasExplicit &&
          currentPosition !== undefined && {
            lastViewedPosition: currentPosition,
          }),
        ...(searchOptions && { searchOptions }),
      };

      const existingHistory = this.getSearchHistory();
      const filteredHistory = existingHistory.filter((h) => h.query !== query);
      const newHistory = [searchHistory, ...filteredHistory.slice(0, 19)];

      localStorage.setItem(this.SEARCH_HISTORY_KEY, JSON.stringify(newHistory));

      this.saveLastSession(searchHistory);

      console.log('Search history saved:', searchHistory);
    } catch (error) {
      console.error('Error saving search history:', error);
    }
  }

  /**
   * Save last session for automatic resumption
   */
  saveLastSession(searchHistory: SearchHistory): void {
    try {
      localStorage.setItem(this.LAST_SESSION_KEY, JSON.stringify(searchHistory));
      console.log('Last session saved:', searchHistory.query);
    } catch (error) {
      console.error('Error saving last session:', error);
    }
  }

  /**
   * Get last session from localStorage
   */
  getLastSession(): SearchHistory | null {
    try {
      const saved = localStorage.getItem(this.LAST_SESSION_KEY);
      return saved ? JSON.parse(saved) : null;
    } catch (error) {
      console.error('Error loading last session:', error);
      return null;
    }
  }

  /**
   * Get search history from localStorage
   */
  getSearchHistory(): SearchHistory[] {
    try {
      const saved = localStorage.getItem(this.SEARCH_HISTORY_KEY);
      return saved ? JSON.parse(saved) : [];
    } catch (error) {
      console.error('Error loading search history:', error);
      return [];
    }
  }

  /**
   * Update last viewed position for a specific search query
   */
  updateSearchPosition(query: string, position: number, currentPage: number, totalItemsLoaded: number, videoId?: number): void {
    try {
      const history = this.getSearchHistory();
      const existingIndex = history.findIndex((h) => h.query === query);

      if (existingIndex >= 0 && history[existingIndex].hasExplicitOrder) {
        history[existingIndex].lastViewedPosition = position;
        if (videoId !== undefined) {
          history[existingIndex].lastViewedVideoId = videoId;
        }

        history[existingIndex].currentPage = currentPage;
        history[existingIndex].totalItemsLoaded = totalItemsLoaded;
        history[existingIndex].timestamp = Date.now();
        localStorage.setItem(this.SEARCH_HISTORY_KEY, JSON.stringify(history));
        console.log(`Updated position for "${query}" to ${position}${videoId ? ` (video ID: ${videoId})` : ''} (page ${currentPage}, ${totalItemsLoaded} total loaded)`);
      }
    } catch (error) {
      console.error('Error updating search position:', error);
    }
  }

  /**
   * Clear search history and last session
   */
  clearSearchHistory(): void {
    try {
      localStorage.removeItem(this.SEARCH_HISTORY_KEY);
      localStorage.removeItem(this.LAST_SESSION_KEY);
      console.log('Search history and last session cleared');
    } catch (error) {
      console.error('Error clearing search history:', error);
    }
  }

  /**
   * Find a history entry by query
   */
  findByQuery(query: string): SearchHistory | undefined {
    return this.getSearchHistory().find((h) => h.query === query);
  }

  /**
   * Find a history entry matching both query and search options.
   * Position resume is only valid when the full search context matches.
   */
  findByFullContext(query: string, searchOptions?: SearchOptions): SearchHistory | undefined {
    return this.getSearchHistory().find((h) => {
      if (h.query !== query) return false;
      return this.searchOptionsMatch(h.searchOptions, searchOptions);
    });
  }

  /**
   * Compare two SearchOptions objects for equivalence.
   * Treats undefined/empty-string/null as equivalent for each field.
   */
  private searchOptionsMatch(a?: SearchOptions, b?: SearchOptions): boolean {
    if (!a && !b) return true;
    if (!a || !b) {
      // One is missing — treat as match only if the other has all default/empty values
      const opts = a || b;
      return this.isDefaultOptions(opts!);
    }

    const dateMode = (o: SearchOptions) => o.advancedDateMode || 'none';
    if (dateMode(a) !== dateMode(b)) return false;

    const mode = dateMode(a);
    if (mode === 'period') {
      if ((a.periodUnit || 'week') !== (b.periodUnit || 'week')) return false;
      if ((a.periodOffset ?? 0) !== (b.periodOffset ?? 0)) return false;
    } else if (mode === 'exact') {
      if ((a.exactGranularity || 'day') !== (b.exactGranularity || 'day')) return false;
      if (a.exactYear !== b.exactYear) return false;
      if (a.exactMonth !== b.exactMonth) return false;
      if (a.exactDay !== b.exactDay) return false;
    } else if (mode === 'range') {
      if ((a.rangeGranularity || 'month') !== (b.rangeGranularity || 'month')) return false;
      if (a.rangeFromYear !== b.rangeFromYear) return false;
      if (a.rangeFromMonth !== b.rangeFromMonth) return false;
      if (a.rangeFromDay !== b.rangeFromDay) return false;
      if (a.rangeToYear !== b.rangeToYear) return false;
      if (a.rangeToMonth !== b.rangeToMonth) return false;
      if (a.rangeToDay !== b.rangeToDay) return false;
    }

    if ((a.orderBy || '') !== (b.orderBy || '')) return false;
    if ((a.minScore ?? null) !== (b.minScore ?? null)) return false;
    if ((a.maxScore ?? null) !== (b.maxScore ?? null)) return false;

    return true;
  }

  /**
   * Check if a SearchOptions object represents all default/empty values
   */
  private isDefaultOptions(opts: SearchOptions): boolean {
    const mode = opts.advancedDateMode || 'none';
    if (mode !== 'none') return false;
    if (opts.orderBy && opts.orderBy !== '') return false;
    if (opts.minScore != null) return false;
    if (opts.maxScore != null) return false;
    return true;
  }
}
